import { useEffect } from "react";

import appEventBus from "@/app/app-event-bus.mjs";
import globals from "@/util/global-whitelist.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";

export const EVENT_DROPDOWN_TOGGLE = "EVENT_DROPDOWN_TOGGLE";

type DropdownRefCallbacks<T> = {
  onClickOutside: (node: T) => void;
  onWindowResize: (node: T) => void;
  onAssignNode: (node: T) => void;
};

export default function useDropdownRef<T extends Node>({
  onClickOutside = () => {},
  onWindowResize = () => {},
  onAssignNode = () => {},
}: DropdownRefCallbacks<T>) {
  const ref = useCallbackRef(
    (node: T) => {
      // Wait for DOM to settle first.
      globals.requestAnimationFrame(() => {
        onAssignNode(node);
      });

      function handleMouseDown(event: MouseEvent) {
        if (!(event.target instanceof Node)) return;
        if (node && !node.contains(event.target)) {
          onClickOutside(node);
        }
      }

      function handleResize() {
        onWindowResize(node);
      }

      globals.addEventListener("resize", handleResize);
      globals.addEventListener("mousedown", handleMouseDown);

      return () => {
        globals.removeEventListener("mousedown", handleMouseDown);
        globals.removeEventListener("resize", handleResize);
      };
    },
    [onClickOutside, onWindowResize, onAssignNode],
  );

  useEffect(() => {
    const listener = () => {
      onClickOutside(ref.current);
    };

    appEventBus.on(EVENT_DROPDOWN_TOGGLE, listener);

    return () => {
      appEventBus.off(EVENT_DROPDOWN_TOGGLE, listener);
    };
  }, []);

  return ref;
}
